import React, { useState, useEffect } from 'react';
import { 
  Box, 
  VStack, 
  Text, 
  Button,
  HStack,
  Alert,
  AlertIcon,
  Progress,
  Badge,
  Card,
  CardBody,
  Flex,
  Icon,
  useToast,
  SimpleGrid,
  useColorModeValue,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Center,
  Spinner
} from '@chakra-ui/react';
import { FiFileText, FiDollarSign, FiShield, FiCheck, FiPenTool } from 'react-icons/fi';
import EmbeddedSignatureFlow from '../../../components/signature/EmbeddedSignatureFlow';

import { RegistrationData } from '../index';

interface BestInClassSignatureStepProps {
  data: RegistrationData;
  updateData: (data: Partial<RegistrationData>) => void;
  onNext: () => void;
  onPrevious: () => void;
}

interface DocumentProgress {
  documentType: 'W9_FORM' | 'ACH_AUTHORIZATION' | 'VENDOR_AGREEMENT';
  title: string;
  description: string;
  benefit: string;
  icon: any;
  color: string;
  completed: boolean;
  signatureRequestId?: string;
}

const BestInClassSignatureStep: React.FC<BestInClassSignatureStepProps> = ({
  data,
  updateData,
  onNext,
  onPrevious
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const toast = useToast();
  const progressBg = useColorModeValue('gray.100', 'gray.700');
  const completionSuccessBg = useColorModeValue('green.50', 'green.900');

  // Initialize signatures object if it doesn't exist
  const signatures = data.signatures || {
    w9: { completed: false },
    ach: { completed: false },
    terms: { completed: false }
  };

  // Check if vendor is US-based (for W9 and ACH requirements)
  const vendorLocation = data.companyInfo?.vendorLocation || 'United States';
  const isUSBased = vendorLocation === 'United States';

  // Build documents array conditionally based on vendor location
  const allDocuments: DocumentProgress[] = [
    // W9 - US only
    ...(isUSBased ? [{
      documentType: 'W9_FORM' as const,
      title: 'W-9 Tax Form',
      description: 'IRS tax classification and taxpayer identification',
      benefit: 'Required for payments and tax compliance',
      icon: FiFileText,
      color: 'blue' as const,
      completed: signatures.w9?.completed || false,
      signatureRequestId: signatures.w9?.signatureRequestId
    }] : []),
    // ACH - US only  
    ...(isUSBased ? [{
      documentType: 'ACH_AUTHORIZATION' as const,
      title: 'ACH Authorization',
      description: 'Electronic payment authorization',
      benefit: 'Enables fast, secure electronic payments',
      icon: FiDollarSign,
      color: 'green' as const,
      completed: signatures.ach?.completed || false,
      signatureRequestId: signatures.ach?.signatureRequestId
    }] : []),
    // Vendor Agreement - Always required
    {
      documentType: 'VENDOR_AGREEMENT' as const,
      title: 'Vendor Agreement',
      description: 'Partnership terms and conditions',
      benefit: 'Establishes our business relationship',
      icon: FiShield,
      color: 'purple' as const,
      completed: signatures.terms?.completed || false,
      signatureRequestId: signatures.terms?.signatureRequestId
    }
  ];

  const documents = allDocuments;

  const completedCount = documents.filter(doc => doc.completed).length;
  const progressPercentage = (completedCount / documents.length) * 100;
  const allCompleted = completedCount === documents.length;

  // Set initial active tab to first unsigned document and handle document array changes
  useEffect(() => {
    const firstUnsignedIndex = documents.findIndex(doc => !doc.completed);
    
    // If active tab is out of bounds due to document array changes, reset it
    if (activeTab >= documents.length) {
      setActiveTab(firstUnsignedIndex !== -1 ? firstUnsignedIndex : 0);
    } 
    // If no active tab set or current tab is completed, move to first unsigned
    else if (firstUnsignedIndex !== -1 && (activeTab === -1 || documents[activeTab]?.completed)) {
      setActiveTab(firstUnsignedIndex);
    }
  }, [documents, activeTab]);

  // Prepare signer info from registration data
  const signerInfo = {
    name: `${data.contactInfo?.primaryContact?.firstName || ''} ${data.contactInfo?.primaryContact?.lastName || ''}`.trim() || 'Unknown',
    email: data.contactInfo?.primaryContact?.email || 'noemail@example.com'
  };

  const handleDocumentComplete = (documentType: string, signatureRequestId: string) => {
    console.log(`✅ ${documentType} signature completed:`, signatureRequestId);
    
    // Update the signatures data
    const updatedSignatures = {
      ...signatures,
      [getSignatureKey(documentType)]: {
        completed: true,
        signatureRequestId: signatureRequestId,
        completedAt: new Date().toISOString()
      }
    };

    updateData({ signatures: updatedSignatures });

    const document = documents.find(doc => doc.documentType === documentType);
    toast({
      title: 'Document Signed!',
      description: `${document?.title} has been signed successfully.`,
      status: 'success',
      duration: 5000,
      isClosable: true,
    });

    // Move to next unsigned document tab
    const nextDocIndex = documents.findIndex(doc => 
      !updatedSignatures[getSignatureKey(doc.documentType)]?.completed
    );
    
    if (nextDocIndex !== -1) {
      setTimeout(() => {
        setActiveTab(nextDocIndex);
        toast({
          title: 'Next Document Ready',
          description: `Please sign ${documents[nextDocIndex].title}`,
          status: 'info',
          duration: 3000,
          isClosable: true,
        });
      }, 2000);
    }
  };

  const handleSignatureError = (error: string) => {
    console.error(`❌ Signature failed:`, error);
    
    toast({
      title: 'Signature System Deployment In Progress',
      description: 'The signature integration is being deployed. Please contact support for manual document signing in the meantime.',
      status: 'warning',
      duration: 10000,
      isClosable: true,
    });
  };

  const getSignatureKey = (documentType: string): keyof typeof signatures => {
    switch (documentType) {
      case 'W9_FORM': return 'w9';
      case 'ACH_AUTHORIZATION': return 'ach';
      case 'VENDOR_AGREEMENT': return 'terms';
      default: return 'w9';
    }
  };

  const handleNext = () => {
    if (allCompleted) {
      updateData({ signaturesCompleted: true });
      onNext();
    } else {
      toast({
        title: 'Signatures Required',
        description: 'Please complete all document signatures before proceeding.',
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <VStack spacing={8} align="stretch">
      {/* Progress Card */}
      <Card>
        <CardBody p={8}>
          <VStack spacing={6} align="stretch">
            <HStack spacing={3}>
              <Icon as={FiPenTool} color="blue.500" boxSize={5} />
              <Text fontSize="lg" fontWeight="semibold">
                Signature Progress
              </Text>
              <Badge colorScheme={allCompleted ? 'green' : 'blue'} variant="solid" ml="auto">
                {completedCount} of {documents.length} completed
              </Badge>
            </HStack>

            {/* Location-based document info */}
            {!isUSBased && (
              <Alert status="info" borderRadius="md">
                <AlertIcon />
                <Text fontSize="sm">
                  As a {vendorLocation === 'Canada' ? 'Canadian' : 'international'} vendor, 
                  you only need to sign the Vendor Agreement. US-specific tax forms (W-9) and 
                  payment authorizations (ACH) are not required.
                </Text>
              </Alert>
            )}

            <Box>
              <Progress 
                value={progressPercentage} 
                colorScheme="blue" 
                size="lg" 
                bg={progressBg}
                borderRadius="full"
              />
              <Text fontSize="sm" color="gray.600" mt={2} textAlign="center">
                {Math.round(progressPercentage)}% Complete
              </Text>
            </Box>

            {/* Document Grid */}
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
              {documents.map((doc, index) => {
                const isActive = index === activeTab;
                const borderColor = doc.completed ? 'green.400' : isActive ? `${doc.color}.400` : 'gray.200';
                const bgColor = doc.completed ? 'green.50' : isActive ? `${doc.color}.50` : 'gray.50';
                
                return (
                  <Card 
                    key={doc.documentType}
                    borderWidth={2}
                    borderColor={borderColor}
                    bg={bgColor}
                    cursor={!doc.completed ? 'pointer' : 'default'}
                    onClick={() => !doc.completed && setActiveTab(index)}
                    _hover={!doc.completed ? { transform: 'translateY(-2px)', shadow: 'lg' } : {}}
                    transition="all 0.2s"
                  >
                    <CardBody p={6}>
                      <VStack spacing={4} align="center">
                        <Flex
                          w={12}
                          h={12}
                          bg={doc.completed ? 'green.500' : isActive ? `${doc.color}.500` : 'gray.300'}
                          borderRadius="full"
                          align="center"
                          justify="center"
                        >
                          <Icon 
                            as={doc.completed ? FiCheck : doc.icon} 
                            color="white" 
                            boxSize={6} 
                          />
                        </Flex>
                        
                        <VStack spacing={2} align="center">
                          <Text fontWeight="bold" textAlign="center">
                            {doc.title}
                          </Text>
                          <Text fontSize="sm" color="gray.600" textAlign="center">
                            {doc.description}
                          </Text>
                          <Text fontSize="xs" color="blue.600" textAlign="center" fontWeight="medium">
                            {doc.benefit}
                          </Text>
                        </VStack>

                        <Badge 
                          colorScheme={doc.completed ? 'green' : isActive ? doc.color : 'gray'}
                          variant={doc.completed ? 'solid' : 'outline'}
                        >
                          {doc.completed ? 'Completed' : isActive ? 'Active' : 'Pending'}
                        </Badge>
                      </VStack>
                    </CardBody>
                  </Card>
                );
              })}
            </SimpleGrid>
          </VStack>
        </CardBody>
      </Card>

      {/* Tabbed Document Signatures */}
      {!allCompleted && (
        <Card>
          <CardBody p={8}>
            <VStack spacing={6} align="stretch">
              <Tabs 
                index={activeTab} 
                onChange={setActiveTab}
                variant="unstyled"
              >
                <TabPanels>
                  {documents.map((doc, index) => (
                    <TabPanel key={doc.documentType} p={6}>
                      <VStack spacing={6} align="stretch">
                        {/* Document Content */}
                        {doc.completed ? (
                          <Center p={8}>
                            <VStack spacing={4}>
                              <Icon as={FiCheck} color="green.500" boxSize={12} />
                              <Text fontSize="lg" fontWeight="bold" color="green.600">
                                Document Signed Successfully!
                              </Text>
                              <Badge colorScheme="green" variant="solid" p={2}>
                                Signature ID: {doc.signatureRequestId?.substring(0, 8)}...
                              </Badge>
                            </VStack>
                          </Center>
                        ) : (
                          <Box>
                            {/* Only render EmbeddedSignatureFlow for the active tab */}
                            {index === activeTab ? (
                              <EmbeddedSignatureFlow
                                documentType={doc.documentType}
                                vendorData={data}
                                signerInfo={signerInfo}
                                onComplete={(signatureRequestId) => handleDocumentComplete(doc.documentType, signatureRequestId)}
                                onError={handleSignatureError}
                                isActive={true}
                              />
                            ) : (
                              <Center p={12}>
                                <VStack spacing={4}>
                                  <Icon as={doc.icon} color={`${doc.color}.400`} boxSize={16} />
                                  <Text fontSize="lg" color="gray.600">
                                    Click this tab to sign {doc.title}
                                  </Text>
                                  <Badge colorScheme={doc.color} variant="outline">
                                    {doc.benefit}
                                  </Badge>
                                </VStack>
                              </Center>
                            )}
                          </Box>
                        )}
                      </VStack>
                    </TabPanel>
                  ))}
                </TabPanels>
              </Tabs>
            </VStack>
          </CardBody>
        </Card>
      )}

      {/* All Completed Message */}
      {allCompleted && (
        <Card bg={completionSuccessBg} borderColor="green.200" borderWidth={2}>
          <CardBody p={8}>
            <VStack spacing={4} align="center">
              <Flex
                w={16}
                h={16}
                bg="green.500"
                borderRadius="full"
                align="center"
                justify="center"
              >
                <Icon as={FiCheck} color="white" boxSize={8} />
              </Flex>
              
              <VStack spacing={2} align="center">
                <Text fontSize="xl" fontWeight="bold" color="green.700">
                  All Documents Signed Successfully!
                </Text>
                <Text color="green.600" textAlign="center">
                  You have completed all required signature documents. Your vendor registration is nearly complete.
                </Text>
              </VStack>

              <Button 
                colorScheme="green" 
                size="lg"
                onClick={handleNext}
                px={8}
              >
                Continue to Final Steps
              </Button>
            </VStack>
          </CardBody>
        </Card>
      )}


    </VStack>
  );
};

export default BestInClassSignatureStep; 